import { type DragEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import routes from '#routes/index.js'
import { ACTION_INTENTS } from '../../../../types'

export const useUploader = ({
	onUpload,
}: {
	onUpload: (url: string) => void
}) => {
	const [loading, setLoading] = useState(false)
	const { postId } = useParams()

	const uploadFile = useCallback(
		async (file: File) => {
			setLoading(true)
			try {
				if (!postId) {
					throw new Error('Post ID is required')
				}

				let url = ''

				const formData = new FormData()
				formData.append('photoFile', file)
				formData.append('action', 'upload')
				formData.append('postId', postId)
				formData.append('intent', ACTION_INTENTS.UPLOAD_IMAGE)

				const response = await fetch(
					routes.admin.blog.posts.api.uploadImage.route({}),
					{
						method: 'POST',
						body: formData,
					},
				)

				if (response.ok) {
					const res = (await response.json()) as {
						result: {
							image: string
						}
					}

					url = `/resources/post-images/${res.result.image}`
				} else {
					console.log('Error uploading image')
				}

				if (!url) throw new Error('Image could not be uploaded')
				onUpload(url)
			} catch (errPayload: any) {
				console.error(errPayload)
				const error =
					errPayload?.response?.data?.error || 'Algo no ha funcionado.'
				toast.error(error)
			}
			setLoading(false)
		},
		[onUpload, postId],
	)

	const selectFile = useCallback(
		(url: string) => {
			onUpload(url)
		},
		[onUpload],
	)

	return { loading, uploadFile, selectFile }
}

export const useFileUpload = () => {
	const fileInput = useRef<HTMLInputElement>(null)

	const handleUploadClick = useCallback(() => {
		fileInput.current?.click()
	}, [])

	return { ref: fileInput, handleUploadClick }
}

export const useDropZone = ({
	uploader,
}: {
	uploader: (file: File) => void
}) => {
	const [isDragging, setIsDragging] = useState<boolean>(false)
	const [draggedInside, setDraggedInside] = useState<boolean>(false)

	useEffect(() => {
		const dragStartHandler = () => {
			setIsDragging(true)
		}

		const dragEndHandler = () => {
			setIsDragging(false)
		}

		document.body.addEventListener('dragstart', dragStartHandler)
		document.body.addEventListener('dragend', dragEndHandler)

		return () => {
			document.body.removeEventListener('dragstart', dragStartHandler)
			document.body.removeEventListener('dragend', dragEndHandler)
		}
	}, [])

	const onDrop = useCallback(
		(e: DragEvent<HTMLDivElement>) => {
			setDraggedInside(false)
			if (e.dataTransfer.files.length === 0) {
				return
			}

			const fileList = e.dataTransfer.files

			const files: File[] = []

			for (let i = 0; i < fileList.length; i += 1) {
				const item = fileList.item(i)
				if (item) {
					files.push(item)
				}
			}

			if (files.some((file) => file.type.indexOf('image') === -1)) {
				return
			}

			e.preventDefault()

			const filteredFiles = files.filter((f) => f.type.indexOf('image') !== -1)

			const file = filteredFiles.length > 0 ? filteredFiles[0] : undefined

			if (file) {
				uploader(file)
			}
		},
		[uploader],
	)

	const onDragEnter = () => {
		setDraggedInside(true)
	}

	const onDragLeave = () => {
		setDraggedInside(false)
	}

	return { isDragging, draggedInside, onDragEnter, onDragLeave, onDrop }
}
